/**
 * React hook that fetches the current user's building inventory.
 * Components should consume this rather than calling listBuildings() directly.
 */
import { useEffect, useRef, useState } from "react";
import {
  listBuildings,
  type Building,
  type ListBuildingsParams,
} from "./building";

export type BuildingsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; buildings: Building[] };

export function useBuildings(
  params: ListBuildingsParams = {},
  refreshKey = 0,
): BuildingsState {
  const [state, setState] = useState<BuildingsState>({ status: "loading" });
  // Stable ref so the effect doesn't re-run when params identity changes
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    listBuildings(paramsRef.current).then(
      (buildings) => {
        if (!cancelled) setState({ status: "ok", buildings });
      },
      (err: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Unknown error",
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return state;
}
